// ---------------------------------------------------------------------------
// Pure evaluation of one task's stock claim against the CAD and the Logistics
// inventory levels ("Stock from CAD" panel).
//
// A claim answers three independent questions:
//   1. Ceiling — the CAD holds a fixed set of ELEMENTS of each type on the
//      task's floors, and a claim names the ones it covers: a task cannot
//      cover a slab another task already holds (on any floor — an element id
//      is global), or one a CAD revision removed, whatever unit it orders in.
//      The quantity ceiling (`ceiling`) is what the free elements come to in
//      the claim's own unit, and is null whenever the CAD cannot measure that
//      unit at all.
//   2. Stock — matched by folded material name against the Logistics
//      inventory snapshot, same fold rule the stock/delivery dialogs use.
//   3. Drift — has a newer CAD version since taken away elements the claim
//      already booked? This is reported (`cadChanged`) rather than folded into
//      `outcome`, because the banner that surfaces it owns the message; the
//      stock outcome is still evaluated normally underneath it.
//
// The rows handed in are ALREADY grouped per element type across storeys (see
// `useTaskStock`, the one grouping seam) and already narrowed to the claim's
// own material.
//
// No React, no store — unit-tested in isolation.
// ---------------------------------------------------------------------------

import type { ITakeoffMaterial } from '@interfaces/ICadTakeoff';
import type { ITaskStockClaim } from '@interfaces/IScheduler';
import type { InventoryLevel } from '@interfaces/ILogistics';
import { estimatedOnHand, sameUnit } from '@d-orca/shared/client/logistics';
import { fold } from '../../LogisticsPage/units';
import type { ClaimQuantityShare } from './claimIndex';
import {
  claimableCeiling,
  elementConflicts,
  unitKeyOfLabel,
  type ClaimUnitKey,
  type IElementConflict,
} from './claimElements';

export interface IClaimContext {
  claim: ITaskStockClaim;
  /** The claim material's take-off rows on the task's floors, already grouped
   *  to one row per element type. Empty for a free-text claim — then no
   *  ceiling and no element check apply. */
  rows: ITakeoffMaterial[];
  /** Other tasks' claims for the same material whose floors overlap the
   *  task's — the quantity side, for the "already claimed" read-out. */
  otherClaims: ClaimQuantityShare[];
  /** The elements other tasks hold of the material on ANY floor, by globalId
   *  → task name (`takenElements`) — what actually reduces which elements
   *  are left. */
  taken: Map<string, string>;
  /** The claim's unit as a key the CAD understands, null for anything else
   *  (kg, bags…) — `unitKeyOfLabel`. */
  unitKey: ClaimUnitKey | null;
  inventory: InventoryLevel[];
  currentCadVersionId: string | null;
}

export type ClaimOutcome =
  | { kind: 'idle' }
  | { kind: 'over'; elementKind: string; conflicting: number; takenBy: string | null }
  | { kind: 'none'; need: number }
  | { kind: 'unit-mismatch'; level: InventoryLevel }
  | { kind: 'ok'; level: InventoryLevel; remains: number }
  | { kind: 'partial'; level: InventoryLevel; short: number };

export interface IClaimEvaluation {
  /** Total still claimable in the claim's unit; null when the CAD cannot
   *  measure that unit (and for every free-text claim). */
  ceiling: number | null;
  ceilingChecked: boolean;
  claimedByOthers: number;
  /** Every element type the claim can no longer fully have; empty when it fits. */
  conflicts: IElementConflict[];
  outcome: ClaimOutcome;
  /** The first conflicting type, reported only when a NEWER CAD version is
   *  what moved the floor under the claim — the banner owns the message. */
  cadChanged: IElementConflict | null;
}

/** To the tenth every recorded amount is kept in. */
export const round1 = (n: number): number => Math.round(n * 10) / 10;

/** What the claim still has to source, in its unit: the whole quantity with
 *  no stock on site, the shortfall with some, nothing for every other read
 *  (covered, blank, over the ceiling, or a unit the stock disagrees with —
 *  none of which an order answers). What a task's SHARE of an order line is
 *  measured against (contract v13). */
export function needOf(outcome: ClaimOutcome): number {
  if (outcome.kind === 'none') return outcome.need;
  if (outcome.kind === 'partial') return outcome.short;
  return 0;
}

const IDLE_EVALUATION: IClaimEvaluation = {
  ceiling: null,
  ceilingChecked: false,
  claimedByOthers: 0,
  conflicts: [],
  outcome: { kind: 'idle' },
  cadChanged: null,
};

function stockOutcome(inventory: InventoryLevel[], claim: ITaskStockClaim): ClaimOutcome {
  const level = inventory.find((l) => fold(l.materialName) === fold(claim.materialName));
  if (!level) return { kind: 'none', need: claim.quantity };
  if (!sameUnit(level.unit, claim.unit)) return { kind: 'unit-mismatch', level };
  // Against what is on site NOW (the count drawn down at its rate), never the
  // raw last count — the two disagree more every day since it was taken. A
  // row estimated to have run out (or waiting on a check) holds nothing, so
  // it reads as no stock rather than "partly in stock, 0 on site".
  const onHand = estimatedOnHand(level);
  if (onHand <= 0) return { kind: 'none', need: claim.quantity };
  const remains = round1(onHand - claim.quantity);
  return remains >= 0
    ? { kind: 'ok', level, remains }
    : { kind: 'partial', level, short: round1(-remains) };
}

export function evaluateClaim(ctx: IClaimContext): IClaimEvaluation {
  const { claim, rows, taken, unitKey } = ctx;
  if (claim.quantity <= 0 || claim.unit === '' || claim.materialName === '') {
    return IDLE_EVALUATION;
  }

  const claimedByOthers = ctx.otherClaims.reduce((sum, o) => sum + o.quantity, 0);
  const ceiling = claimableCeiling(rows, taken, unitKey);
  // With no rows there is nothing to check against — a free-text claim, or a
  // CAD one whose material the take-off no longer names at all. Both keep
  // whatever they hold rather than being reported over a ceiling that does not
  // exist; a type missing from a material that IS still in the take-off is a
  // different case, and `elementConflicts` does flag that one.
  const conflicts = rows.length === 0 ? [] : elementConflicts(rows, claim.elements ?? [], taken);
  const first = conflicts[0] ?? null;

  // A claim that no longer fits AND is stamped to an older CAD version did not
  // get there by the user typing too much — the model moved under it. That is
  // the banner's story (spec §8), so it is reported separately and the stock
  // outcome is still evaluated normally underneath.
  const cadChanged = first !== null && claim.cadVersionId !== ctx.currentCadVersionId ? first : null;

  // The element ceiling is a hard limit: covering an element the task cannot
  // have wins over any stock read. The one exception is a claim the user
  // explicitly KEPT after a CAD version change (spec §8's "Keep") — that
  // decision is deliberate and logged, so the check skips it for as long as
  // `cadVersionId` stays stamped to the version it was kept against; a FURTHER
  // CAD change moves `currentCadVersionId` again and `cadChanged` fires above.
  const keptOverCurrentCad =
    claim.keptOverCad === true && claim.cadVersionId === ctx.currentCadVersionId;
  const outcome: ClaimOutcome =
    first !== null && cadChanged === null && !keptOverCurrentCad
      ? { kind: 'over', elementKind: first.kind, conflicting: first.conflicting, takenBy: first.takenBy }
      : stockOutcome(ctx.inventory, claim);

  return { ceiling, ceilingChecked: ceiling !== null, claimedByOthers, conflicts, outcome, cadChanged };
}

/**
 * Builds the `IClaimContext` for one claim and evaluates it — the shared
 * "claim → ceiling/stock verdict" pipeline that both `useTaskStock` (a single
 * task's own claims) and `useStockFlags` (every task on the site) need. Takes
 * the material's GROUPED take-off rows and the other tasks' claims already
 * resolved by the caller rather than resolving them itself, so this stays a
 * pure function with no query/store access of its own — the two call sites
 * differ only in how they gather those inputs.
 */
export function evaluateClaimIn(
  claim: ITaskStockClaim,
  rows: ITakeoffMaterial[],
  otherClaims: ClaimQuantityShare[],
  taken: Map<string, string>,
  inventory: InventoryLevel[],
  currentCadVersionId: string | null,
): IClaimEvaluation {
  return evaluateClaim({
    claim,
    // A "Not in the CAD" claim is never matched back to a take-off row, even
    // when its typed name happens to equal one.
    rows: claim.fromCad ? rows : [],
    otherClaims,
    taken,
    unitKey: unitKeyOfLabel(claim.unit),
    inventory,
    currentCadVersionId,
  });
}
